import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type {
	AlertInfoModel,
	OptimizationGroupModel,
	OptimizationGroupReviewModel,
	OptimizationModel,
	SalesEntityModel,
	SalesLevelModel,
	ScheduleModel,
} from '@/shared/generated'
import type { OnChangeFn } from '@tanstack/react-table'
import type { OptimizationGroupTableRow } from '../model/types'

import React, {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
} from 'react'
import { all, find, indexBy, pipe, prop } from 'lodash/fp'

import { getOptStatus } from '@/entities/optimization'

import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { filterByRules } from '@/shared/tools/filtering'
import { searchData } from '@/shared/tools/page-data-helpers'

import { getUpdatingOptimizationsCount } from '../lib/getUpdatingOptimizationsCount'

type GridContext = {
	dataList: OptimizationGroupTableRow[]
	data: OptimizationGroupTableRow[]
	isLoading: boolean
	updatingCount: number
	rowSelection: SRT_RowSelectionState
	setRowSelection: OnChangeFn<SRT_RowSelectionState>
	selectionCount: number
	getSelectedItems(): OptimizationGroupTableRow[]
	productGroupsMap: Record<string, string[]>
}

const getAlertsMapByOgId = (alerts: AlertInfoModel[]) => {
	const alertsMap = {} as Record<string, number>
	for (const alert of alerts) {
		if (alert.optimization_group_id) {
			alertsMap[alert.optimization_group_id] =
				(alertsMap[alert.optimization_group_id] ?? 0) + alert.products
		}
	}

	return alertsMap
}

export const OptimizationGroupsContext = createContext({} as GridContext)
export const useOptimizationGroupsContext = () => {
	const context = use(OptimizationGroupsContext)
	if (!context) {
		throw new Error(
			'useOptimizationGroupsContext must be used within OptimizationGroupsProvider'
		)
	}

	return context
}
const getScheduleByOgId = pipe([prop('sales_levels'), indexBy(prop('level'))])
export function OptimizationGroupsProvider({
	children,
	data: {
		optimizationGroups,
		schedules,
		optimizations,
		salesEntities,
		productGroupsMap,
		reviews,
		alerts,
	},
}: {
	children: React.ReactNode
	data: {
		optimizationGroups: OptimizationGroupModel[]
		schedules: ScheduleModel[]
		optimizations: Record<string, OptimizationModel>
		salesEntities: {
			sales_levels: SalesLevelModel[]
			sales_entities: SalesEntityModel[]
		}
		productGroupsMap: Record<string, string[]>
		reviews: OptimizationGroupReviewModel[]
		alerts: {
			by_id: AlertInfoModel[]
			by_level: AlertInfoModel[]
		}
	}
}) {
	const [{ filters, search }] = useLocationQuery()

	const alertsMap = useMemo(
		() => getAlertsMapByOgId(alerts?.by_level ?? []),
		[alerts]
	)
	const salesLevelsMap = useMemo(
		() => getScheduleByOgId(salesEntities),
		[salesEntities]
	)

	const optimizationGroupsList = useMemo(
		() =>
			optimizationGroups.map((group) => {
				const optimization = optimizations[group.id]
				const schedule = schedules.find((schedule) =>
					schedule.optimization_groups.includes(group.id)
				)
				const review = find({ optimization_group_id: group.id }, reviews ?? [])
				const notifications_count = alertsMap?.[group.id] || 0
				const sales_level_name = salesLevelsMap?.[group.sales_level_id]?.name

				return {
					group,
					optimization,
					schedule,
					review,

					// calculated fields
					notifications_count,
					sales_level_name,
					product_groups: productGroupsMap?.[group.id] || [],
				}
			}),
		[
			optimizationGroups,
			productGroupsMap,
			optimizations,
			schedules,
			reviews,
			alertsMap,
			salesLevelsMap,
		]
	)

	const isLoading = false

	const [rowSelection, setRowSelection] = useState<SRT_RowSelectionState>({})
	const updatingCount = getUpdatingOptimizationsCount(
		optimizationGroupsList?.map(
			({ optimization }) => optimization as OptimizationModel
		)
	)

	// All optimization groups
	const data: OptimizationGroupTableRow[] = useMemo(
		() => enrichData(optimizationGroupsList),
		[optimizationGroupsList]
	)

	// Optimization groups filtered by text and tier and filters
	const dataList = useMemo(
		() => filterByRules(filters, searchData(data, search, 'group.name')) || [],
		[filters, data, search]
	)

	const getSelectedItems = useCallback(
		() =>
			dataList.filter(pipe([getOptGroupId, (id: string) => rowSelection[id]])),
		[rowSelection, dataList]
	)

	return (
		<OptimizationGroupsContext
			value={{
				data,
				dataList,
				rowSelection,
				selectionCount: Object.keys(rowSelection).length,
				setRowSelection,
				getSelectedItems,
				isLoading,
				updatingCount,
				productGroupsMap,
			}}
		>
			{children}
		</OptimizationGroupsContext>
	)
}

const enrichWithUiStatus = (data: Partial<OptimizationGroupTableRow>[]) =>
	data.map((d) => ({ ...d, ui_status: getOptStatus(d?.optimization?.status) }))

const enrichWithReviewers = (data: Partial<OptimizationGroupTableRow>[]) =>
	data.map((d) => ({
		...d,
		reviewers: d?.review?.reviewers?.map(({ id }) => id),
	}))
const enrichWithReviewStatus = (data: Partial<OptimizationGroupTableRow>[]) =>
	data.map((d) => ({
		...d,
		review_completed:
			d?.review && all({ is_approved: true }, d?.review?.reviewers),
	}))
const enrichData = pipe([
	enrichWithReviewStatus,
	enrichWithReviewers,
	enrichWithUiStatus,
])
const getOptGroupId = ({ group }: OptimizationGroupTableRow) => group.id
